import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, Input } from '@agms/ui'
import { forgotPasswordApi } from '../lib/api'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import agmsLogo from '../assets/logos/agms-logo-on-white.svg'

export function ForgotPasswordPage() {
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (email: string) => forgotPasswordApi.requestLink(email),
    onSuccess: (result) => {
      setMessage(result.message)
      setError(null)
    },
    onError: (err) => setError(apiErrorMessage(err, 'Unable to send a reset link.')),
  })

  useEffect(() => {
    document.title = 'Forgot password · AGMS CRM'
  }, [])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    const form = new FormData(event.currentTarget)
    mutation.mutate(String(form.get('email') ?? ''))
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md overflow-hidden">
        <div className="h-1 bg-accent-500" />
        <CardHeader className="items-center text-center">
          <img src={agmsLogo} alt="AGMS" className="h-12 w-auto" />
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Forgot password</h1>
        </CardHeader>
        <CardContent>
          {message ? (
            <p className="text-sm text-gray-700" role="status">{message}</p>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <Input
                name="email"
                label="Email address"
                type="email"
                autoComplete="email"
                required
                placeholder="you@agms.com"
              />
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
              <Button type="submit" className="w-full" isLoading={mutation.isPending}>
                Send reset link
              </Button>
            </form>
          )}
          <Link to="/login" className="mt-4 block text-center text-sm text-blue-600 hover:text-blue-800">
            Back to sign in
          </Link>
        </CardContent>
      </Card>
    </main>
  )
}
